import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Space, Tag, Input, Switch, message } from 'antd';
import { ClearOutlined, PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import io from 'socket.io-client';
import { API_ORIGIN } from '../api';

// Socket URL = API base without /api
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || API_ORIGIN;

const LiveChatLog = () => {
  const [logs, setLogs] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [filter, setFilter] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const logsEndRef = useRef(null);
  const socketRef = useRef(null);

  useEffect(() => {
    // Connect to Socket.IO
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      setIsConnected(true);
      message.success('Connected to live log stream');
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
      message.warning('Disconnected from log stream');
    });

    socketRef.current.on('chat_log', (logData) => {
      if (!isPaused) {
        setLogs(prev => [...prev, {
          timestamp: new Date(logData.timestamp || Date.now()),
          level: logData.level || 'info',
          message: logData.message || 'Log entry',
          details: logData.details || null,
          type: logData.type || 'log'
        }].slice(-1000)); // Keep last 1000 logs
      }
    });

    socketRef.current.on('message_received', (data) => {
      if (!isPaused) {
        setLogs(prev => [...prev, {
          timestamp: new Date(),
          level: 'info',
          message: `📨 Received message from: ${data.from}`,
          details: `Message: ${data.body}`,
          type: 'message'
        }].slice(-1000));
      }
    });

    socketRef.current.on('message_processing', (data) => {
      if (!isPaused) {
        setLogs(prev => [...prev, {
          timestamp: new Date(data.timestamp || Date.now()),
          level: 'info',
          message: `🧠 Processing message...`,
          details: `Agent: ${data.agentName || 'Unknown'} (${data.routingType || 'unknown'}), Functions: ${data.functions?.join(', ') || 'none'}`,
          type: 'processing'
        }].slice(-1000));
      }
    });

    socketRef.current.on('function_call', (data) => {
      if (!isPaused) {
        setLogs(prev => [...prev, {
          timestamp: new Date(),
          level: 'info',
          message: `🔧 Calling function: ${data.functionName}`,
          details: JSON.stringify(data.args, null, 2),
          type: 'function'
        }].slice(-1000));
      }
    });

    socketRef.current.on('function_result', (data) => {
      if (!isPaused) {
        setLogs(prev => [...prev, {
          timestamp: new Date(),
          level: data.success ? 'success' : 'error',
          message: `✅ Function result: ${data.functionName}`,
          details: JSON.stringify(data.result, null, 2),
          type: 'function_result'
        }].slice(-1000));
      }
    });

    socketRef.current.on('agent_response', (data) => {
      if (!isPaused) {
        const agentInfo = `Agent: ${data.agentName || 'Unknown'}${data.routingType ? ` (${data.routingType})` : ''}`;
        setLogs(prev => [...prev, {
          timestamp: new Date(data.timestamp || Date.now()),
          level: 'success',
          message: `💬 Agent response sent`,
          details: `${agentInfo}\n\nResponse:\n${data.response}`,
          type: 'response'
        }].slice(-1000));
      }
    });

    socketRef.current.on('thinking_step', (data) => {
      if (!isPaused) {
        setLogs(prev => [...prev, {
          timestamp: new Date(),
          level: 'info',
          message: `💭 Thinking: ${data.detail}`,
          details: `Step: ${data.step}, Chat: ${data.chatId}`,
          type: 'thinking'
        }].slice(-1000));
      }
    });

    socketRef.current.on('error', (error) => {
      if (!isPaused) {
        setLogs(prev => [...prev, {
          timestamp: new Date(),
          level: 'error',
          message: `❌ Error: ${error.message || error}`,
          details: JSON.stringify(error, null, 2),
          type: 'error'
        }].slice(-1000));
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [isPaused]);

  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const clearLogs = () => {
    setLogs([]);
  };

  const getLogColor = (level) => {
    switch (level) {
      case 'error': return '#ff4d4f';
      case 'success': return '#52c41a';
      case 'warning': return '#faad14';
      default: return '#1890ff';
    }
  };

  const getLogIcon = (type) => {
    switch (type) {
      case 'message': return '📨';
      case 'processing': return '🧠';
      case 'function': return '🔧';
      case 'function_result': return '✅';
      case 'response': return '💬';
      case 'thinking': return '💭';
      case 'error': return '❌';
      default: return 'ℹ️';
    }
  };

  const filteredLogs = logs.filter(log => 
    log.message.toLowerCase().includes(filter.toLowerCase()) ||
    (log.details && log.details.toLowerCase().includes(filter.toLowerCase()))
  );

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <span>Live Chat Logs</span>
            <Tag color={isConnected ? 'green' : 'red'}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </Tag>
          </Space>
        }
        extra={
          <Space>
            <Input
              placeholder="Filter logs..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
            <Switch
              checkedChildren="Auto-scroll"
              unCheckedChildren="Pause scroll"
              checked={autoScroll}
              onChange={setAutoScroll}
            />
            <Button
              icon={isPaused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
              onClick={() => setIsPaused(!isPaused)}
            >
              {isPaused ? 'Resume' : 'Pause'}
            </Button>
            <Button icon={<ClearOutlined />} onClick={clearLogs}>
              Clear
            </Button>
          </Space>
        }
      >
        <div
          style={{
            height: 'calc(100vh - 250px)',
            overflow: 'auto',
            backgroundColor: '#001529',
            padding: '16px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '12px',
          }}
        >
          {filteredLogs.length === 0 ? (
            <div style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
              {isConnected ? 'Waiting for logs...' : 'Not connected to server'}
            </div>
          ) : (
            filteredLogs.map((log, index) => (
              <div
                key={index}
                style={{
                  marginBottom: '8px',
                  padding: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '4px',
                  borderLeft: `3px solid ${getLogColor(log.level)}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span>{getLogIcon(log.type)}</span>
                  <span style={{ color: getLogColor(log.level), fontWeight: 'bold' }}>
                    [{log.level.toUpperCase()}]
                  </span>
                  <span style={{ color: '#888' }}>
                    {new Date(log.timestamp).toLocaleTimeString('en-US', { 
                      hour12: false, 
                      hour: '2-digit', 
                      minute: '2-digit', 
                      second: '2-digit',
                      fractionalSecondDigits: 3
                    })}
                  </span>
                </div>
                <div style={{ color: '#fff', marginBottom: '4px' }}>
                  {log.message}
                </div>
                {log.details && (
                  <details style={{ color: '#aaa', marginTop: '4px' }}>
                    <summary style={{ cursor: 'pointer', color: '#888' }}>Details</summary>
                    <pre style={{ 
                      marginTop: '8px', 
                      padding: '8px',
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '4px',
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}>
                      {log.details}
                    </pre>
                  </details>
                )}
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      </Card>
    </div>
  );
};

export default LiveChatLog;

